import { memo, useCallback } from 'react';
import { KeyRound, Mail, MailX, Pencil, Send, UserCheck, UserX } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';

import { MEMBER_ACTION } from '../../constants/memberActions';
import MenuAction from '../shared/MenuAction';

/** The design system row has a 260px minimum; the prototype's row menu is 220px wide. */
const COMPACT_ROW = '!min-w-[13.75rem]';

/** Destructive entries of the menu read in red (the sent link stops working). */
const DANGER_ROW = '[&_*]:!text-red-600 dark:[&_*]:!text-red-400';

const ACTION_ICON = Object.freeze({
  [MEMBER_ACTION.RESET_PASSWORD]: KeyRound,
  [MEMBER_ACTION.SUSPEND]: UserX,
  [MEMBER_ACTION.REACTIVATE]: UserCheck,
  [MEMBER_ACTION.SEND_INVITATION]: Send,
  [MEMBER_ACTION.RESEND_INVITATION]: Send,
  [MEMBER_ACTION.EDIT_EMAIL]: Pencil,
  [MEMBER_ACTION.REVOKE_INVITATION]: MailX,
});

/**
 * One entry of a Members row menu: icon, label and, when blocked, its reason. A stable `onAction`
 * keeps the entry from re-rendering with its row.
 *
 * @param {object} props
 * @param {object} props.member UI member the menu belongs to.
 * @param {{ id: string, labelKey: string, enabled: boolean, disabledReasonKey?: string }} props.action From `getMemberRowActions`.
 * @param {(actionId: string, member: object) => void} props.onAction
 */
const MemberRowMenuAction = ({ member, action, onAction }) => {
  const { t } = useTranslation('people-access');
  const handleSelect = useCallback(() => onAction(action.id, member), [onAction, action.id, member]);

  return (
    <MenuAction
      label={t(action.labelKey)}
      size="small"
      className={action.id === MEMBER_ACTION.REVOKE_INVITATION ? `${COMPACT_ROW} ${DANGER_ROW}` : COMPACT_ROW}
      icon={ACTION_ICON[action.id] ?? Mail}
      disabled={!action.enabled}
      disabledReason={action.disabledReasonKey ? t(action.disabledReasonKey) : undefined}
      onSelect={handleSelect}
    />
  );
};

export default memo(MemberRowMenuAction);
